import { Phone, Mail, MapPin, Clock, Navigation } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const MAP_GEOCODE = "ul. Industrialna 58, 9704 Shumen, Bulgaria";
const MAP_EMBED = `https://www.google.com/maps?q=${encodeURIComponent(MAP_GEOCODE)}&z=17&output=embed`;
const MAP_DIRECTIONS = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(MAP_GEOCODE)}`;

export const Contact = () => {
  const { t } = useLanguage();
  return (
    <section id="contact" data-testid="contact-section" className="relative carbon grain text-white border-t border-zinc-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.25em] text-red-500 mb-4">{t.contact.eyebrow}</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tighter leading-[1.1] mb-8">
              {t.contact.headingPre}<span className="text-red-500">{t.contact.headingAccent}</span>{t.contact.headingPost}
            </h2>
            <p className="text-lg text-zinc-300 leading-relaxed max-w-md mb-12">
              {t.contact.body}
            </p>

            <div className="space-y-6">
              <a
                href="tel:+359892600604"
                data-testid="contact-phone-link"
                className="flex items-center gap-5 group"
              >
                <span className="w-14 h-14 bg-red-600 group-hover:bg-red-500 flex items-center justify-center shrink-0 transition-colors shadow-[0_0_25px_rgba(239,68,68,0.4)]">
                  <Phone size={22} strokeWidth={2} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-wide text-zinc-300">{t.contact.phoneLabel}</p>
                  <p className="font-display font-bold text-xl tracking-tight">+359 892 600 604</p>
                </div>
              </a>

              <a
                href="mailto:megastream@abv.bg"
                data-testid="contact-email-link"
                className="flex items-center gap-5 group"
              >
                <span className="w-14 h-14 bg-zinc-900 border border-zinc-800 group-hover:border-red-500 flex items-center justify-center shrink-0 transition-colors">
                  <Mail size={22} strokeWidth={2} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-wide text-zinc-300">{t.contact.emailLabel}</p>
                  <p className="font-display font-bold text-xl tracking-tight">megastream@abv.bg</p>
                </div>
              </a>

              <a
                href={MAP_DIRECTIONS}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-address-link"
                className="flex items-center gap-5 group"
              >
                <span className="w-14 h-14 bg-zinc-900 border border-zinc-800 group-hover:border-red-500 flex items-center justify-center shrink-0 transition-colors">
                  <MapPin size={22} strokeWidth={2} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-wide text-zinc-300">{t.contact.addressLabel}</p>
                  <p className="font-display font-bold text-xl tracking-tight leading-snug">
                    {t.contact.addressLine1}
                  </p>
                  <p className="text-sm text-zinc-300">{t.contact.addressLine2}</p>
                </div>
              </a>

              <div className="flex items-center gap-5">
                <span className="w-14 h-14 bg-zinc-900 border border-zinc-800 flex items-center justify-center shrink-0">
                  <Clock size={22} strokeWidth={2} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-wide text-zinc-300">{t.contact.hoursLabel}</p>
                  <p className="font-display font-bold text-xl tracking-tight leading-snug">{t.contact.hoursWeek}</p>
                  <p className="text-sm text-zinc-300">{t.contact.hoursWeekend}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative min-h-[350px] lg:min-h-full map-frame group overflow-hidden">
            <iframe
              title={t.contact.mapTitle}
              data-testid="contact-map"
              src={MAP_EMBED}
              className="absolute inset-0 w-full h-full transition-transform duration-[600ms] ease-out group-hover:scale-105"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            <a
              href={MAP_DIRECTIONS}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="map-navigate-btn"
              className="absolute bottom-4 right-4 flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white px-5 py-3 font-bold uppercase tracking-wide text-sm transition-colors shadow-[0_0_25px_rgba(239,68,68,0.5)]"
            >
              <Navigation size={16} strokeWidth={2.5} /> {t.contact.navigate}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
